/**
 * FinanceProfitTab — a aba **Lucro** (DRE) do módulo financeiro.
 *
 * Mostra a DECOMPOSIÇÃO, não só o número final — mesma filosofia do
 * `QuoteTotalsBreakdown`: um "Lucro: R$ 1.240,00" solto não permite ao usuário
 * conferir nada nem descobrir de onde veio o erro quando o número o surpreende.
 *
 * ─── OS DOIS PORTÕES, NESTA ORDEM (armadilha #38) ───────────────────────────
 *
 * 1. **PAPEL** — `/financeiro` inteiro já exige `canSeeFinance`, então quem
 *    chega aqui é owner/admin. (O gate de papel vem antes por princípio: para um
 *    `member` o problema não é plano, e assinar não resolveria.)
 * 2. **PLANO** — a aba é PRO por `reports_advanced`. O módulo financeiro ser
 *    grátis NÃO abre o relatório por herança (armadilha #54): é exatamente o
 *    mesmo arranjo do Relatório de Pagamentos, que também lê o financeiro
 *    grátis e continua PRO. Reusar a flag existente em vez de criar uma nova
 *    mantém uma promessa só para o usuário ("relatórios avançados são PRO").
 *
 * ─── AS BASES DE DATA FICAM ROTULADAS ───────────────────────────────────────
 *
 * Receitas e despesas vêm do CAIXA (base escolhida pelo usuário); o CMV vem da
 * COMPETÊNCIA (data do documento). Cada bloco diz de onde veio — é o que evita
 * o "despesas R$ 0,00 aqui e custo R$ 52,00 ali, no mesmo mês" sem explicação.
 */
import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, CalendarClock, Info, Settings2, TrendingUp } from 'lucide-react';
import { useFeature } from '@/hooks/useFeature';
import { useFinanceEntriesRaw } from '@/hooks/useFinance';
import { useProfitDocuments } from '@/hooks/useProfitDocuments';
import { formatBRL } from '@/lib/financeLabels';
import { AwaitingData, BlurOverlay } from '@/components/reports/ReportGates';
import { DateRangeFilter, type DateRange } from '@/components/reports/DateRangeFilter';
import type { ReportPeriodPreset } from '@/lib/reportPeriod';
import { companyWeekStartsOn } from '@/lib/reportPeriod';
import { useCompany } from '@/contexts/useCompany';
import { toIsoDateRange, type PaymentDateBasis } from '@/lib/paymentsReport';
import {
  COGS_BASIS_LABEL,
  COGS_SCOPE_LABEL,
  computeProfitReport,
  type ProfitReport,
  type ProfitSettings,
} from '@/lib/profitReport';
import {
  useFinanceCategoriesReadOnly,
  useProfitSettings,
} from '@/hooks/useProfitSettings';

const QUOTE_STATUS_OPTIONS = [
  ['pending', 'Pendente'],
  ['sent', 'Enviado'],
  ['approved', 'Aprovado'],
  ['completed', 'Concluído'],
  ['rejected', 'Rejeitado'],
  ['cancelled', 'Cancelado'],
] as const;
const SERVICE_ORDER_STATUS_OPTIONS = [
  ['pending', 'Pendente'],
  ['in_progress', 'Em andamento'],
  ['completed', 'Concluída'],
  ['cancelled', 'Cancelada'],
] as const;

/**
 * Amostra para o usuário grátis — o padrão da casa é mostrar o relatório
 * BORRADO com números plausíveis, nunca uma tela de zeros (que pareceria "não
 * tenho lucro nenhum" em vez de "isto é PRO").
 */
const SAMPLE_REPORT: ProfitReport = {
  revenue: 12400,
  revenueCount: 18,
  cogs: 4820,
  billableExpenses: 640,
  billableExpensesCount: 3,
  grossProfit: 6940,
  otherExpenses: 2380,
  otherExpensesCount: 9,
  netProfit: 4560,
  netMargin: 36.8,
  cogsDetail: {
    total: 4820,
    costedItemCount: 26,
    missingCostCount: 0,
    documentCount: 14,
    skippedFromQuoteCount: 0,
  },
  isEmpty: false,
};

interface Props {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  basis: PaymentDateBasis;
  onBasisChange: (basis: PaymentDateBasis) => void;
}

/** Uma linha da decomposição. `emphasis` marca os dois subtotais. */
function Line({
  label,
  value,
  hint,
  sign,
  emphasis,
}: {
  label: string;
  value: number;
  hint?: string;
  sign?: '+' | '−';
  emphasis?: boolean;
}) {
  return (
    <div
      className={
        emphasis
          ? 'flex items-baseline gap-2 border-t pt-2 text-sm font-semibold'
          : 'flex items-baseline gap-2 text-sm'
      }
    >
      <div className="min-w-0 flex-1">
        <p className={emphasis ? '' : 'text-muted-foreground'}>
          {sign ? `(${sign}) ` : ''}
          {label}
        </p>
        {hint && <p className="text-[11px] leading-snug text-muted-foreground/80">{hint}</p>}
      </div>
      <span
        className={
          emphasis && value < 0 ? 'shrink-0 tabular-nums text-destructive' : 'shrink-0 tabular-nums'
        }
      >
        {formatBRL(value)}
      </span>
    </div>
  );
}

export function FinanceProfitTab({
  dateRange,
  onDateRangeChange,
  basis,
  onBasisChange,
}: Props) {
  const feature = useFeature('reports_advanced');
  const { activeCompany } = useCompany();
  const { settings, save: saveSettings } = useProfitSettings(feature.allowed);
  const { categories } = useFinanceCategoriesReadOnly(feature.allowed);
  const expenseCategories = useMemo(
    () => categories.filter((category: any) => category.kind === 'expense'),
    [categories],
  );
  const [settingsDraft, setSettingsDraft] = useState<ProfitSettings>(settings);
  useEffect(() => setSettingsDraft(settings), [settings]);
  /**
   * Preset ativo do filtro. Fica LOCAL porque quem guarda a faixa é a
   * `FinancePage` e nenhum outro pedaço desta aba precisa saber qual preset foi
   * escolhido — só o próprio seletor, para rotular a si mesmo. O padrão é o
   * mesmo do `profitRange` inicial (últimos 6 meses).
   */
  const [preset, setPreset] = useState<ReportPeriodPreset>('6m');
  const range = useMemo(
    () => toIsoDateRange(dateRange.from, dateRange.to),
    [dateRange.from, dateRange.to],
  );

  const { entries, isLoading: entriesLoading } = useFinanceEntriesRaw();
  // Só busca os documentos quando o plano libera: um usuário grátis vê a
  // amostra borrada e não precisa (nem deve) pagar o custo da leitura.
  const {
    documents,
    truncated,
    isLoading: docsLoading,
    isError: docsError,
  } = useProfitDocuments(range, feature.allowed);

  const computed = useMemo(
    () => computeProfitReport(entries, documents, range, basis, settings),
    [entries, documents, range, basis, settings],
  );
  const report = feature.allowed ? computed : SAMPLE_REPORT;

  const cashLabel =
    basis === 'paid'
      ? 'Pela data em que o dinheiro entrou ou saiu'
      : 'Pela data em que estava previsto (vencimento)';

  const isLoading = entriesLoading || docsLoading;
  const toggleStatus = (
    field: 'quoteCogsStatuses' | 'serviceOrderCogsStatuses',
    status: string,
  ) => {
    setSettingsDraft((current) => ({
      ...current,
      [field]: current[field].includes(status)
        ? current[field].filter((value) => value !== status)
        : [...current[field], status],
    }));
  };
  const toggleExpenseCategory = (categoryId: string) => {
    setSettingsDraft((current) => {
      const selected = current.otherExpenseCategoryIds ?? [];
      return {
        ...current,
        otherExpenseCategoryIds: selected.includes(categoryId)
          ? selected.filter((id) => id !== categoryId)
          : [...selected, categoryId],
      };
    });
  };

  return (
    <div className="space-y-4">
      <DateRangeFilter
        dateRange={dateRange}
        onDateRangeChange={onDateRangeChange}
        preset={preset}
        onPresetChange={setPreset}
        weekStartsOn={companyWeekStartsOn(activeCompany)}
      />

      {/* Base de data — escolha EXPLÍCITA, e a tela diz o que ela afeta. */}
      <Card className="bg-muted/30">
        <CardContent className="space-y-2 p-3">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 shrink-0 text-muted-foreground" />
            <Select value={basis} onValueChange={(v) => onBasisChange(v as PaymentDateBasis)}>
              <SelectTrigger className="h-9 flex-1 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="paid">Quando o dinheiro entrou ou saiu</SelectItem>
                <SelectItem value="due">Quando estava previsto (vencimento)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-[11px] leading-snug text-muted-foreground">
            <span className="font-medium">O que entrou e o que saiu</span> conta pelo dinheiro
            ({cashLabel.toLowerCase()}).{' '}
            <span className="font-medium">O custo do que você vendeu</span> conta pela data do
            orçamento ou da ordem de serviço. São duas contagens diferentes de propósito: por isso
            um mês pode ter custo sem ter despesa paga.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-3">
          <div className="flex items-start gap-2">
            <Settings2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">Como o lucro está sendo calculado</p>
              <p className="text-xs text-muted-foreground">
                Custo: {settings.quoteCogsStatuses.length} situações de orçamento e{' '}
                {settings.serviceOrderCogsStatuses.length} de ordem de serviço. Outras despesas:{' '}
                {settings.otherExpenseCategoryIds === null
                  ? 'todas as não cobráveis'
                  : settings.otherExpenseCategoryIds.length === 1
                    ? `1 categoria${settings.includeUncategorizedOtherExpenses ? ' + sem categoria' : ''}`
                    : `${settings.otherExpenseCategoryIds.length} categorias${settings.includeUncategorizedOtherExpenses ? ' + sem categoria' : ''}`}.
              </p>
            </div>
          </div>
          <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs leading-snug text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              Mudar isto altera também os meses passados: muda o cálculo do lucro, não só
              o que aparece na tela agora.
            </p>
          </div>

          <details className="rounded-md border p-3">
            <summary className="cursor-pointer text-sm font-medium">
              Alterar o cálculo
            </summary>
            <div className="mt-4 space-y-5">
              <div className="space-y-2">
                <Label>Orçamentos que entram no custo</Label>
                <div className="grid grid-cols-2 gap-2">
                  {QUOTE_STATUS_OPTIONS.map(([value, label]) => (
                    <label key={value} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={settingsDraft.quoteCogsStatuses.includes(value)}
                        onCheckedChange={() => toggleStatus('quoteCogsStatuses', value)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Ordens de serviço que entram no custo</Label>
                <div className="grid grid-cols-2 gap-2">
                  {SERVICE_ORDER_STATUS_OPTIONS.map(([value, label]) => (
                    <label key={value} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={settingsDraft.serviceOrderCogsStatuses.includes(value)}
                        onCheckedChange={() => toggleStatus('serviceOrderCogsStatuses', value)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>O que conta como Outras Despesas</Label>
                <Select
                  value={settingsDraft.otherExpenseCategoryIds === null ? 'all' : 'selected'}
                  onValueChange={(value) =>
                    setSettingsDraft((current) => ({
                      ...current,
                      otherExpenseCategoryIds: value === 'all' ? null : [],
                    }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as despesas não cobráveis</SelectItem>
                    <SelectItem value="selected">Somente categorias selecionadas</SelectItem>
                  </SelectContent>
                </Select>
                {settingsDraft.otherExpenseCategoryIds !== null && (
                  <div className="space-y-2 rounded-md border p-2">
                    {expenseCategories.map((category: any) => (
                      <label key={category.id} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={settingsDraft.otherExpenseCategoryIds?.includes(category.id)}
                          onCheckedChange={() => toggleExpenseCategory(category.id)}
                        />
                        {category.name}
                      </label>
                    ))}
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={settingsDraft.includeUncategorizedOtherExpenses}
                        onCheckedChange={(checked) =>
                          setSettingsDraft((current) => ({
                            ...current,
                            includeUncategorizedOtherExpenses: checked === true,
                          }))}
                      />
                      Despesas sem categoria
                    </label>
                  </div>
                )}
              </div>
              <Button
                type="button"
                className="w-full"
                disabled={saveSettings.isPending}
                onClick={() => {
                  if (!feature.require()) return;
                  saveSettings.mutate(settingsDraft);
                }}
              >
                Salvar definições
              </Button>
            </div>
          </details>
        </CardContent>
      </Card>

      <BlurOverlay allowed={feature.allowed} onUnlock={() => feature.require()}>
        {isLoading && feature.allowed ? (
          <Card>
            <CardContent className="p-6">
              <AwaitingData hint="Carregando os documentos do período" />
            </CardContent>
          </Card>
        ) : report.isEmpty && feature.allowed ? (
          <Card>
            <CardContent className="p-6">
              <AwaitingData hint="Nenhum lançamento nem documento neste período" />
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Sem os documentos o CMV sai ZERO e o lucro aparece maior do que
                é. Dizer isso é obrigatório: um número errado sem aviso é pior
                que nenhum número. (Acontece tipicamente offline — os
                documentos do CMV não são cacheados como o financeiro.) */}
            {docsError && feature.allowed && (
              <Card className="border-amber-300 bg-amber-50/60 dark:border-amber-900/50 dark:bg-amber-950/20">
                <CardContent className="flex gap-3 p-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <p className="text-xs leading-snug">
                    Não foi possível ler os orçamentos e ordens de serviço do período
                    (você está sem conexão?). <span className="font-medium">O custo abaixo está
                    zerado e o lucro, maior do que o real.</span> Reconecte e abra a aba de novo.
                  </p>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="h-4 w-4 text-emerald-600" />
                  Resultado do período
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Line
                  label={`Receitas${report.revenueCount ? ` (${report.revenueCount})` : ''}`}
                  value={report.revenue}
                  hint={cashLabel}
                />
                <Line
                  sign="−"
                  label="Custo do que foi vendido"
                  value={report.cogs}
                  hint={COGS_BASIS_LABEL}
                />
                <Line
                  sign="−"
                  label={`Despesas cobráveis${report.billableExpensesCount ? ` (${report.billableExpensesCount})` : ''}`}
                  value={report.billableExpenses}
                  hint="Repasses ao cliente: entram como receita no orçamento e como despesa aqui — margem zero."
                />
                <Line label="Lucro bruto" value={report.grossProfit} emphasis />
                <Line
                  sign="−"
                  label={`Outras despesas${report.otherExpensesCount ? ` (${report.otherExpensesCount})` : ''}`}
                  value={report.otherExpenses}
                  hint={cashLabel}
                />
                <Line label="Lucro líquido" value={report.netProfit} emphasis />

                <p className="pt-1 text-xs text-muted-foreground">
                  {report.netMargin === null
                    ? 'Sem receita no período — não há margem a calcular.'
                    // `toFixed` devolve PONTO decimal; em pt-BR isso lê errado
                    // (armadilha #39). Vírgula, como o resto dos números da tela.
                    : `Margem líquida: ${report.netMargin.toFixed(1).replace('.', ',')}% da receita.`}
                </p>
              </CardContent>
            </Card>

            {/* Ausência de custo NUNCA vira zero: dizer quantos itens ficaram de
                fora é o que impede o lucro de parecer melhor do que é. */}
            {report.cogsDetail.missingCostCount > 0 && (
              <Card className="border-amber-300 bg-amber-50/60 dark:border-amber-900/50 dark:bg-amber-950/20">
                <CardContent className="flex gap-3 p-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <p className="text-xs leading-snug">
                    <span className="font-medium">
                      {report.cogsDetail.missingCostCount}{' '}
                      {report.cogsDetail.missingCostCount === 1 ? 'item' : 'itens'} sem custo informado
                    </span>{' '}
                    {report.cogsDetail.missingCostCount === 1 ? 'ficou' : 'ficaram'} de fora do custo.
                    O lucro acima está <span className="font-medium">maior</span> do que o real.
                    Informe o custo no catálogo para corrigir.
                  </p>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/30">
              <CardContent className="flex gap-3 p-3">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="space-y-1 text-[11px] leading-snug text-muted-foreground">
                  <p>
                    <span className="font-medium">O custo vem de:</span> {COGS_SCOPE_LABEL} Foram
                    considerados {report.cogsDetail.documentCount}{' '}
                    {report.cogsDetail.documentCount === 1 ? 'documento' : 'documentos'} e{' '}
                    {report.cogsDetail.costedItemCount}{' '}
                    {report.cogsDetail.costedItemCount === 1 ? 'item' : 'itens'} com custo.
                  </p>
                  {report.cogsDetail.skippedFromQuoteCount > 0 && (
                    <p>
                      {report.cogsDetail.skippedFromQuoteCount}{' '}
                      {report.cogsDetail.skippedFromQuoteCount === 1
                        ? 'ordem de serviço veio de um orçamento e não foi contada'
                        : 'ordens de serviço vieram de um orçamento e não foram contadas'}{' '}
                      de novo — o custo já está no orçamento de origem.
                    </p>
                  )}
                  {truncated && (
                    <p className="font-medium text-amber-700 dark:text-amber-500">
                      O período tem documentos demais e a leitura foi cortada: os números estão
                      incompletos. Escolha um período menor.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </BlurOverlay>
    </div>
  );
}
